"use client"

import { useState, useEffect } from "react"
import { Card, CardContent } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import { Calendar, Clock, Plus, X, Lock, Info, Edit, Trash2, Waves } from "lucide-react"
import { createClient } from "@/lib/supabase/client"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import { Checkbox } from "@/components/ui/checkbox"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip"
import { useToast } from "@/hooks/use-toast"
import Link from "next/link"
import { usePlatform, triggerHaptic } from "@/hooks/use-platform"
import { getUserProfileUrl } from "@/lib/utils/user-slug"
import { createNotificationWithPush } from "@/lib/utils/push-notifications"

interface PlannedSession {
  id: string
  user_id: string
  planned_date: string
  planned_time: string | null
  planned_start_time?: string | null
  planned_end_time?: string | null
  is_all_day?: boolean
  activity_type?: 'surfing' | 'kitesurfing' | null
  notes: string | null
  users: {
    name: string | null
    first_name: string | null
    last_name: string | null
    email: string | null
    auth_id: string | null
    slug: string | null
    avatar_url: string | null
    surf_experience: string | null
  }
}

interface GroupedSessions {
  [date: string]: PlannedSession[]
}

export function SpotSurfPlans({ spotId, user, onJoinPlan, isPlus }: { spotId: string; user: any; onJoinPlan?: () => void; isPlus?: boolean }) {
  const [sessions, setSessions] = useState<PlannedSession[]>([])
  const [loading, setLoading] = useState(true)
  const [showAddModal, setShowAddModal] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [editingSession, setEditingSession] = useState<PlannedSession | null>(null)
  const [plannedDate, setPlannedDate] = useState("")
  const [plannedStartTime, setPlannedStartTime] = useState("")
  const [plannedEndTime, setPlannedEndTime] = useState("")
  const [isAllDay, setIsAllDay] = useState(false)
  const [activityType, setActivityType] = useState<'surfing' | 'kitesurfing'>('surfing')
  const [spotActivityType, setSpotActivityType] = useState<'surfing' | 'kitesurfing' | 'both' | null>(null)
  const [notes, setNotes] = useState("")
  const [submitting, setSubmitting] = useState(false)
  const [currentUserId, setCurrentUserId] = useState<string | null>(null)
  const { toast } = useToast()
  const supabase = createClient()

  // Fetch spot's activity_type to determine available options
  useEffect(() => {
    const fetchSpotActivityType = async () => {
      const { data, error } = await supabase
        .from("surf_spots")
        .select("activity_type")
        .eq("id", spotId)
        .single()
      
      if (data?.activity_type) {
        setSpotActivityType(data.activity_type as 'surfing' | 'kitesurfing' | 'both')
        // Set default activity type based on spot
        // For 'both' spots, default to 'surfing' (user must choose one activity per session)
        if (data.activity_type === 'both') {
          setActivityType('surfing') // Default to surfing for 'both' spots
        } else {
          setActivityType(data.activity_type as 'surfing' | 'kitesurfing')
        }
      }
    }
    
    fetchSpotActivityType()
  }, [spotId, supabase])

  useEffect(() => {
    if (user) {
      const channel = supabase
        .channel(`spot-plans-${spotId}`)
        .on(
          "postgres_changes",
          {
            event: "*",
            schema: "public",
            table: "planned_surf_sessions",
            filter: `surf_spot_id=eq.${spotId}`,
          },
          () => {
            fetchPlannedSessions()
          },
        )
        .subscribe()

      fetchPlannedSessions()
      getCurrentUser()

      return () => {
        supabase.removeChannel(channel)
      }
    } else {
      const dummySessions: PlannedSession[] = [
        {
          id: "1",
          user_id: "dummy1",
          planned_date: new Date().toISOString().split("T")[0],
          planned_time: "08:00",
          notes: "Early morning session",
          users: { name: "Sarah M.", avatar_url: null, surf_experience: "Intermediate" },
        },
        {
          id: "2",
          user_id: "dummy2",
          planned_date: new Date().toISOString().split("T")[0],
          planned_time: "10:30",
          notes: null,
          users: { name: "Mike T.", avatar_url: null, surf_experience: "Advanced" },
        },
        {
          id: "3",
          user_id: "dummy3",
          planned_date: new Date(Date.now() + 86400000).toISOString().split("T")[0],
          planned_time: "07:00",
          notes: "Dawn patrol!",
          users: { name: "Emma L.", avatar_url: null, surf_experience: "Beginner" },
        },
        {
          id: "4",
          user_id: "dummy4",
          planned_date: new Date(Date.now() + 86400000).toISOString().split("T")[0],
          planned_time: null,
          notes: null,
          users: { name: "Jake R.", avatar_url: null, surf_experience: "Intermediate" },
        },
      ]
      setSessions(dummySessions)
      setLoading(false)
    }
  }, [spotId, user])

  const getCurrentUser = async () => {
    const {
      data: { user: currentUser },
    } = await supabase.auth.getUser()
    
    if (!currentUser) {
      setCurrentUserId(null)
      return
    }
    
    // Fetch users.id (primary key) from users table using auth_id
    // session.user_id references users.id, not users.auth_id
    const { data: userRecord } = await supabase
      .from("users")
      .select("id")
      .eq("auth_id", currentUser.id)
      .single()
    
    setCurrentUserId(userRecord?.id || null)
  }

  const fetchPlannedSessions = async () => {
    try {
      const { data, error } = await supabase
        .from("planned_surf_sessions")
        .select(
          `
          id,
          user_id,
          planned_date,
          planned_time,
          planned_start_time,
          planned_end_time,
          is_all_day,
          activity_type,
          notes,
          users (
            name,
            first_name,
            last_name,
            email,
            auth_id,
            avatar_url,
            surf_experience
          )
        `,
        )
        .eq("surf_spot_id", spotId)
        .gte("planned_date", new Date().toISOString().split("T")[0])
        .order("planned_date", { ascending: true })
        .order("planned_time", { ascending: true })

      if (error) throw error
      setSessions(data || [])
    } catch (error) {
      console.error("Error fetching planned sessions:", error)
    } finally {
      setLoading(false)
    }
  }

  // Helper function to get display name from user data
  const getUserDisplayName = (user: PlannedSession['users']): string => {
    if (user.name) return user.name
    if (user.first_name || user.last_name) {
      return [user.first_name, user.last_name].filter(Boolean).join(' ')
    }
    if (user.email) {
      // Extract username from email (part before @)
      return user.email.split('@')[0]
    }
    return "Unknown User"
  }

  const handleAddPlan = async () => {
    if (!plannedDate) {
      triggerHaptic('error')
      toast({
        title: "Date required",
        description: "Please select a date for your surf session",
        variant: "destructive",
      })
      return
    }

    // Validate times if not all-day
    if (!isAllDay) {
      if (!plannedStartTime || !plannedEndTime) {
        triggerHaptic('error')
        toast({
          title: "Validation Error",
          description: "Please enter both start and end times, or check 'All Day'.",
          variant: "destructive",
        })
        return
      }

      // Validate end time is after start time
      const startDateTime = new Date(`${plannedDate}T${plannedStartTime}`)
      const endDateTime = new Date(`${plannedDate}T${plannedEndTime}`)
      
      if (endDateTime <= startDateTime) {
        triggerHaptic('error')
        toast({
          title: "Validation Error",
          description: "End time must be after start time.",
          variant: "destructive",
        })
        return
      }
    }

    setSubmitting(true)
    triggerHaptic('impact')
    try {
      const {
        data: { user: currentUser },
      } = await supabase.auth.getUser()
      if (!currentUser) throw new Error("Not authenticated")

      // Fetch users.id (primary key) and user profile data from users table using auth_id
      // planned_surf_sessions.user_id references users.id, not users.auth_id
      const { data: userRecord, error: userError } = await supabase
        .from("users")
        .select("id, name, first_name, last_name, email, auth_id, avatar_url, surf_experience")
        .eq("auth_id", currentUser.id)
        .single()

      if (userError || !userRecord) {
        throw new Error("User profile not found")
      }

      // Build insert data
      const insertData: any = {
        surf_spot_id: spotId,
        user_id: userRecord.id, // Use users.id (primary key), not auth_id
        planned_date: plannedDate,
        is_all_day: isAllDay,
        activity_type: activityType,
        notes: notes || null,
      }

      // Add times if not all-day
      if (!isAllDay && plannedStartTime && plannedEndTime) {
        insertData.planned_start_time = plannedStartTime
        insertData.planned_end_time = plannedEndTime
        // Keep planned_time for backward compatibility (use start time)
        insertData.planned_time = plannedStartTime
      } else {
        insertData.planned_time = null
        insertData.planned_start_time = null
        insertData.planned_end_time = null
      }

      // Create optimistic session for immediate UI update
      const optimisticSession: PlannedSession = {
        id: `temp-${Date.now()}`, // Temporary ID
        user_id: userRecord.id,
        planned_date: plannedDate,
        planned_time: insertData.planned_time,
        planned_start_time: insertData.planned_start_time,
        planned_end_time: insertData.planned_end_time,
        is_all_day: isAllDay,
        activity_type: activityType,
        notes: notes || null,
        users: {
          name: userRecord.name,
          first_name: userRecord.first_name,
          last_name: userRecord.last_name,
          email: userRecord.email,
          auth_id: userRecord.auth_id,
          avatar_url: userRecord.avatar_url,
          surf_experience: userRecord.surf_experience,
        },
      }

      // Add optimistic session to state immediately
      setSessions((prev) => [...prev, optimisticSession])

      const { data: insertedData, error } = await supabase
        .from("planned_surf_sessions")
        .insert(insertData)
        .select(
          `
          id,
          user_id,
          planned_date,
          planned_time,
          planned_start_time,
          planned_end_time,
          is_all_day,
          activity_type,
          notes,
          users (
            name,
            first_name,
            last_name,
            email,
            auth_id,
            avatar_url,
            surf_experience
          )
        `
        )
        .single()

      if (error) {
        // Remove optimistic session on error
        setSessions((prev) => prev.filter((s) => s.id !== optimisticSession.id))
        throw error
      }

      // Replace optimistic session with real data from database
      if (insertedData) {
        triggerHaptic('success')
        setSessions((prev) =>
          prev.map((s) => (s.id === optimisticSession.id ? insertedData : s))
        )

        // Notify friends and nearby users about the new plan
        try {
          // Get access token for authentication
          const { data: { session } } = await supabase.auth.getSession()
          const accessToken = session?.access_token

          fetch("/api/notifications/notify-activity", {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              ...(accessToken ? { "Authorization": `Bearer ${accessToken}` } : {})
            },
            body: JSON.stringify({
              type: 'planned_session',
              spotId: spotId,
              activityType: activityType === 'kitesurfing' ? 'kite' : 'surf',
              relatedId: insertedData.id,
              plannedDate: plannedDate,
              isPublic: true
            }),
          }).catch((error) => {
            console.error("Error notifying friends and nearby users:", error)
          })
        } catch (notifError) {
          console.warn("Failed to notify friends:", notifError)
        }
      }

      toast({
        title: "Plan added!",
        description: "Your surf session has been added to the schedule",
      })

      setShowAddModal(false)
      setPlannedDate("")
      setPlannedStartTime("")
      setPlannedEndTime("")
      setIsAllDay(false)
      // Reset activity_type based on spot's activity_type
      // For 'both' spots, default to 'surfing' (user must choose one activity per session)
      if (spotActivityType === 'both') {
        setActivityType('surfing')
      } else if (spotActivityType) {
        setActivityType(spotActivityType)
      } else {
        setActivityType('surfing') // Default fallback
      }
      setNotes("")
    } catch (error) {
      console.error("Error adding plan:", error)
      toast({
        title: "Error",
        description: "Failed to add your surf plan",
        variant: "destructive",
      })
    } finally {
      setSubmitting(false)
    }
  }

  const handleEditPlan = (session: PlannedSession) => {
    setEditingSession(session)
    setPlannedDate(session.planned_date)
    setPlannedStartTime(session.planned_start_time || "")
    setPlannedEndTime(session.planned_end_time || "")
    setIsAllDay(session.is_all_day || false)
    setActivityType(session.activity_type || 'surfing')
    setNotes(session.notes || "")
    setShowEditModal(true)
  }

  const handleUpdatePlan = async () => {
    if (!editingSession) return

    if (!plannedDate) {
      triggerHaptic('error')
      toast({
        title: "Date required",
        description: "Please select a date for your surf session",
        variant: "destructive",
      })
      return
    }

    // Validate times if not all-day
    if (!isAllDay) {
      if (!plannedStartTime || !plannedEndTime) {
        triggerHaptic('error')
        toast({
          title: "Validation Error",
          description: "Please enter both start and end times, or check 'All Day'.",
          variant: "destructive",
        })
        return
      }

      // Validate end time is after start time
      const startDateTime = new Date(`${plannedDate}T${plannedStartTime}`)
      const endDateTime = new Date(`${plannedDate}T${plannedEndTime}`)
      
      if (endDateTime <= startDateTime) {
        triggerHaptic('error')
        toast({
          title: "Validation Error",
          description: "End time must be after start time.",
          variant: "destructive",
        })
        return
      }
    }

    setSubmitting(true)
    triggerHaptic('impact')
    try {
      // Build update data
      const updateData: any = {
        planned_date: plannedDate,
        is_all_day: isAllDay,
        activity_type: activityType,
        notes: notes || null,
      }

      // Add times if not all-day
      if (!isAllDay && plannedStartTime && plannedEndTime) {
        updateData.planned_start_time = plannedStartTime
        updateData.planned_end_time = plannedEndTime
        updateData.planned_time = plannedStartTime
      } else {
        updateData.planned_time = null
        updateData.planned_start_time = null
        updateData.planned_end_time = null
      }

      const { error: updateError } = await supabase
        .from("planned_surf_sessions")
        .update(updateData)
        .eq("id", editingSession.id)

      if (updateError) throw updateError

      // Refetch the updated session to get complete data with user info
      const { data: updatedData, error: selectError } = await supabase
        .from("planned_surf_sessions")
        .select(
          `
          id,
          user_id,
          planned_date,
          planned_time,
          planned_start_time,
          planned_end_time,
          is_all_day,
          activity_type,
          notes,
          users (
            name,
            first_name,
            last_name,
            email,
            auth_id,
            avatar_url,
            surf_experience
          )
        `
        )
        .eq("id", editingSession.id)
        .single()

      if (selectError) {
        // If select fails, just refetch all sessions
        await fetchPlannedSessions()
      } else if (updatedData) {
        triggerHaptic('success')
        // Update session in state
        setSessions((prev) =>
          prev.map((s) => (s.id === editingSession.id ? updatedData : s))
        )
      } else {
        // Fallback: refetch all sessions
        await fetchPlannedSessions()
      }

      toast({
        title: "Plan updated!",
        description: "Your surf session has been updated",
      })

      setShowEditModal(false)
      setEditingSession(null)
      setPlannedDate("")
      setPlannedStartTime("")
      setPlannedEndTime("")
      setIsAllDay(false)
      // Reset activity_type based on spot's activity_type
      // For 'both' spots, default to 'surfing' (user must choose one activity per session)
      if (spotActivityType === 'both') {
        setActivityType('surfing')
      } else if (spotActivityType) {
        setActivityType(spotActivityType)
      } else {
        setActivityType('surfing') // Default fallback
      }
      setNotes("")
    } catch (error) {
      console.error("Error updating plan:", error)
      toast({
        title: "Error",
        description: "Failed to update your surf plan",
        variant: "destructive",
      })
    } finally {
      setSubmitting(false)
    }
  }

  const handleRemovePlan = async (sessionId: string) => {
    if (!confirm("Are you sure you want to cancel this planned session?")) {
      return
    }

    try {
      triggerHaptic('impact')
      const { error } = await supabase.from("planned_surf_sessions").delete().eq("id", sessionId)

      if (error) throw error

      triggerHaptic('success')
      // Remove from state
      setSessions((prev) => prev.filter((s) => s.id !== sessionId))

      toast({
        title: "Plan cancelled",
        description: "Your surf session has been cancelled",
      })
    } catch (error) {
      console.error("Error removing plan:", error)
      toast({
        title: "Error",
        description: "Failed to cancel your surf plan",
        variant: "destructive",
      })
    }
  }

  const groupSessionsByDate = (): GroupedSessions => {
    return sessions.reduce((acc, session) => {
      const date = session.planned_date
      if (!acc[date]) {
        acc[date] = []
      }
      acc[date].push(session)
      return acc
    }, {} as GroupedSessions)
  }

  const formatDate = (dateString: string) => {
    const date = new Date(dateString)
    const today = new Date()
    const tomorrow = new Date(today)
    tomorrow.setDate(tomorrow.getDate() + 1)

    if (date.toDateString() === today.toDateString()) {
      return "Today"
    } else if (date.toDateString() === tomorrow.toDateString()) {
      return "Tomorrow"
    } else {
      return date.toLocaleDateString("en-US", { weekday: "long", month: "short", day: "numeric" })
    }
  }

  const groupedSessions = groupSessionsByDate()

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="text-center">
          <Calendar className="w-8 h-8 animate-pulse text-blue-500 mx-auto mb-2" />
          <p className="text-sm text-muted-foreground">Loading surf plans...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="relative">
      <div className={`space-y-6 ${(!user || !isPlus) ? "blur-md pointer-events-none" : ""}`}>
        <div className="flex items-center justify-between pb-4 border-b border-slate-50 dark:border-slate-800/50">
          <p className="text-sm font-medium text-slate-500 dark:text-slate-400 max-w-[180px]">See who's planning to surf and join them!</p>
          {user && (
            <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
              <DialogTrigger asChild>
                <Button 
                  size="sm" 
                  variant="outline"
                  className="rounded-xl border-slate-100 dark:border-slate-800 font-bold text-xs h-10 shadow-sm active:scale-95 transition-all"
                >
                  <Plus className="w-4 h-4 mr-2 text-blue-500" />
                  Add Your Plan
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-white dark:bg-slate-900 border-none rounded-[2rem]">
                <DialogHeader>
                  <DialogTitle className="text-xl font-black uppercase tracking-tight">Plan Your Surf Session</DialogTitle>
                  <DialogDescription className="text-xs font-medium text-slate-500">Let others know when you're planning to surf at this spot</DialogDescription>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <div className="space-y-2">
                    <Label htmlFor="date" className="text-xs font-black uppercase tracking-widest text-slate-400">Date *</Label>
                    <Input
                      id="date"
                      type="date"
                      value={plannedDate}
                      onChange={(e) => setPlannedDate(e.target.value)}
                      className="rounded-xl border-slate-100 dark:border-slate-800 h-12"
                      min={new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().split("T")[0]}
                      max={new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString().split("T")[0]}
                    />
                  </div>

                  {/* Activity Type Selection */}
                  {spotActivityType && (
                    <div className="space-y-2">
                      <Label htmlFor="activity_type">Activity Type *</Label>
                      <div className="flex gap-4">
                        {(spotActivityType === 'surfing' || spotActivityType === 'both') && (
                          <div className="flex items-center space-x-2">
                            <input
                              type="radio"
                              id="activity_surfing"
                              name="activity_type"
                              value="surfing"
                              checked={activityType === 'surfing'}
                              onChange={(e) => setActivityType(e.target.value as 'surfing')}
                              className="w-4 h-4"
                            />
                            <Label htmlFor="activity_surfing" className="cursor-pointer flex items-center gap-1">
                              <Waves className="w-4 h-4" />
                              Surfing
                            </Label>
                          </div>
                        )}
                        {(spotActivityType === 'kitesurfing' || spotActivityType === 'both') && (
                          <div className="flex items-center space-x-2">
                            <input
                              type="radio"
                              id="activity_kitesurfing"
                              name="activity_type"
                              value="kitesurfing"
                              checked={activityType === 'kitesurfing'}
                              onChange={(e) => setActivityType(e.target.value as 'kitesurfing')}
                              className="w-4 h-4"
                            />
                            <Label htmlFor="activity_kitesurfing" className="cursor-pointer flex items-center gap-1">
                              <span className="text-lg">🪁</span>
                              Kitesurfing
                            </Label>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* All Day Checkbox */}
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="is_all_day_plan"
                      checked={isAllDay}
                      onCheckedChange={(checked) => {
                        setIsAllDay(checked === true)
                        if (checked) {
                          setPlannedStartTime("")
                          setPlannedEndTime("")
                        }
                      }}
                    />
                    <div className="flex items-center gap-2">
                      <Label
                        htmlFor="is_all_day_plan"
                        className="text-sm font-normal cursor-pointer"
                      >
                        All Day Session
                      </Label>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Info className="w-4 h-4 text-muted-foreground cursor-help" />
                        </TooltipTrigger>
                        <TooltipContent className="max-w-xs">
                          <p>
                            Specific session times help us improve forecasts by comparing predicted vs actual conditions. 
                            All-day sessions cannot be reviewed but still count toward your session history.
                          </p>
                        </TooltipContent>
                      </Tooltip>
                    </div>
                  </div>

                  {/* Start and End Times */}
                  {!isAllDay && (
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="start_time" className="flex items-center gap-2">
                          <Clock className="w-4 h-4" />
                          Start Time <span className="text-red-500">*</span>
                        </Label>
                        <Input
                          id="start_time"
                          type="time"
                          value={plannedStartTime}
                          onChange={(e) => setPlannedStartTime(e.target.value)}
                          required={!isAllDay}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="end_time" className="flex items-center gap-2">
                          <Clock className="w-4 h-4" />
                          End Time <span className="text-red-500">*</span>
                        </Label>
                        <Input
                          id="end_time"
                          type="time"
                          value={plannedEndTime}
                          onChange={(e) => setPlannedEndTime(e.target.value)}
                          required={!isAllDay}
                        />
                      </div>
                    </div>
                  )}

                  <div className="space-y-2">
                    <Label htmlFor="notes">Notes (optional)</Label>
                    <Textarea
                      id="notes"
                      placeholder="Any details about your session..."
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                      rows={3}
                    />
                  </div>
                </div>
                <div className="flex justify-end gap-2">
                  <Button variant="outline" onClick={() => setShowAddModal(false)}>
                    Cancel
                  </Button>
                  <Button onClick={handleAddPlan} disabled={submitting}>
                    {submitting ? "Adding..." : "Add Plan"}
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          )}
        </div>

        {/* Edit Modal */}
        {user && (
          <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
            <DialogContent className="bg-white dark:bg-slate-800">
              <DialogHeader>
                <DialogTitle>Edit Your Surf Session</DialogTitle>
                <DialogDescription>Update your planned surf session details</DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label htmlFor="edit_date">Date *</Label>
                  <Input
                    id="edit_date"
                    type="date"
                    value={plannedDate}
                    onChange={(e) => setPlannedDate(e.target.value)}
                    min={new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().split("T")[0]}
                    max={new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString().split("T")[0]}
                  />
                  <p className="text-xs text-muted-foreground">
                    Plan sessions up to 7 days in the past or up to 1 year in the future. Conditions will be stored per hour for forecast calibration.
                  </p>
                </div>

                {/* Activity Type Selection */}
                {spotActivityType && (
                  <div className="space-y-2">
                    <Label htmlFor="edit_activity_type">Activity Type *</Label>
                    <div className="flex gap-4">
                      {(spotActivityType === 'surfing' || spotActivityType === 'both') && (
                        <div className="flex items-center space-x-2">
                          <input
                            type="radio"
                            id="edit_activity_surfing"
                            name="edit_activity_type"
                            value="surfing"
                            checked={activityType === 'surfing'}
                            onChange={(e) => setActivityType(e.target.value as 'surfing')}
                            className="w-4 h-4"
                          />
                          <Label htmlFor="edit_activity_surfing" className="cursor-pointer flex items-center gap-1">
                            <Waves className="w-4 h-4" />
                            Surfing
                          </Label>
                        </div>
                      )}
                      {(spotActivityType === 'kitesurfing' || spotActivityType === 'both') && (
                        <div className="flex items-center space-x-2">
                          <input
                            type="radio"
                            id="edit_activity_kitesurfing"
                            name="edit_activity_type"
                            value="kitesurfing"
                            checked={activityType === 'kitesurfing'}
                            onChange={(e) => setActivityType(e.target.value as 'kitesurfing')}
                            className="w-4 h-4"
                          />
                          <Label htmlFor="edit_activity_kitesurfing" className="cursor-pointer flex items-center gap-1">
                            <span className="text-lg">🪁</span>
                            Kitesurfing
                          </Label>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* All Day Checkbox */}
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="edit_is_all_day"
                    checked={isAllDay}
                    onCheckedChange={(checked) => {
                      setIsAllDay(checked === true)
                      if (checked) {
                        setPlannedStartTime("")
                        setPlannedEndTime("")
                      }
                    }}
                  />
                  <div className="flex items-center gap-2">
                    <Label
                      htmlFor="edit_is_all_day"
                      className="text-sm font-normal cursor-pointer"
                    >
                      All Day Session
                    </Label>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Info className="w-4 h-4 text-muted-foreground cursor-help" />
                      </TooltipTrigger>
                      <TooltipContent className="max-w-xs">
                        <p>
                          Specific session times help us improve forecasts by comparing predicted vs actual conditions. 
                          All-day sessions cannot be reviewed but still count toward your session history.
                        </p>
                      </TooltipContent>
                    </Tooltip>
                  </div>
                </div>

                {/* Start and End Times */}
                {!isAllDay && (
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="edit_start_time" className="flex items-center gap-2">
                        <Clock className="w-4 h-4" />
                        Start Time <span className="text-red-500">*</span>
                      </Label>
                      <Input
                        id="edit_start_time"
                        type="time"
                        value={plannedStartTime}
                        onChange={(e) => setPlannedStartTime(e.target.value)}
                        required={!isAllDay}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="edit_end_time" className="flex items-center gap-2">
                        <Clock className="w-4 h-4" />
                        End Time <span className="text-red-500">*</span>
                      </Label>
                      <Input
                        id="edit_end_time"
                        type="time"
                        value={plannedEndTime}
                        onChange={(e) => setPlannedEndTime(e.target.value)}
                        required={!isAllDay}
                      />
                    </div>
                  </div>
                )}

                <div className="space-y-2">
                  <Label htmlFor="edit_notes">Notes (optional)</Label>
                  <Textarea
                    id="edit_notes"
                    placeholder="Any details about your session..."
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    rows={3}
                  />
                </div>
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => {
                  setShowEditModal(false)
                  setEditingSession(null)
                  setPlannedDate("")
                  setPlannedStartTime("")
                  setPlannedEndTime("")
                  setIsAllDay(false)
                  setNotes("")
                }}>
                  Cancel
                </Button>
                <Button onClick={handleUpdatePlan} disabled={submitting}>
                  {submitting ? "Updating..." : "Update Plan"}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        )}

        {Object.keys(groupedSessions).length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 px-6 text-center">
            <div className="w-20 h-20 bg-slate-50 dark:bg-slate-800/50 rounded-full flex items-center justify-center mb-6">
              <Calendar className="w-10 h-10 text-slate-300 dark:text-slate-600" />
            </div>
            <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-2">No upcoming surf sessions planned yet</h3>
            <p className="text-sm text-slate-500 dark:text-slate-400 max-w-[240px]">Be the first to add your plan and meet other surfers at the spot!</p>
          </div>
        ) : (
          <div className="space-y-6">
            {Object.entries(groupedSessions).map(([date, dateSessions]) => (
              <div key={date} className="space-y-3">
                <div className="flex items-center gap-3">
                  <div className="flex items-center gap-2">
                    <Calendar className="w-4 h-4 text-blue-500" />
                    <h3 className="font-semibold text-lg">{formatDate(date)}</h3>
                  </div>
                  <Badge variant="secondary" className="text-xs">
                    {dateSessions.length} {dateSessions.length === 1 ? "surfer" : "surfers"}
                  </Badge>
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
                  {dateSessions.map((session) => (
                    <Card
                      key={session.id}
                      className="relative group hover:shadow-md transition-all border-blue-100 hover:border-blue-200"
                    >
                      <CardContent className="p-4 flex flex-col items-center text-center space-y-2">
                        <div className="relative">
                          <Link
                            href={getUserProfileUrl(session.users.slug || null, session.users.auth_id || undefined)}
                            className="cursor-pointer hover:opacity-80 transition-opacity"
                          >
                            <Avatar className="w-20 h-20 border-2 border-blue-100">
                              <AvatarImage src={session.users.avatar_url || undefined} alt={getUserDisplayName(session.users)} />
                              <AvatarFallback className="bg-gradient-to-br from-blue-400 to-cyan-500 text-white text-xl">
                                {getUserDisplayName(session.users).charAt(0).toUpperCase()}
                              </AvatarFallback>
                            </Avatar>
                          </Link>
                        </div>

                        <div className="space-y-1 w-full">
                          <Link
                            href={getUserProfileUrl(session.users.slug || null, session.users.auth_id || undefined)}
                            className="font-medium text-sm leading-tight line-clamp-2 hover:underline cursor-pointer block"
                          >
                            {getUserDisplayName(session.users)}
                          </Link>

                          {session.users.surf_experience && (
                            <Badge variant="outline" className="text-xs">
                              {session.users.surf_experience}
                            </Badge>
                          )}

                          {/* Activity Type Icons */}
                          {session.activity_type && (
                            <div className="flex items-center justify-center gap-1.5 text-xs">
                              {session.activity_type === 'surfing' && (
                                <div className="flex items-center gap-1" title="Surfing">
                                  <Waves className="w-3.5 h-3.5 text-blue-500" />
                                </div>
                              )}
                              {session.activity_type === 'kitesurfing' && (
                                <div className="flex items-center gap-1" title="Kitesurfing">
                                  <span className="text-base">🪁</span>
                                </div>
                              )}
                            </div>
                          )}

                          {session.is_all_day ? (
                            <div className="flex items-center justify-center gap-1 text-xs text-muted-foreground">
                              <Clock className="w-3 h-3" />
                              All Day
                            </div>
                          ) : session.planned_start_time && session.planned_end_time ? (
                            <div className="flex items-center justify-center gap-1 text-xs text-muted-foreground">
                              <Clock className="w-3 h-3" />
                              {session.planned_start_time.slice(0, 5)} - {session.planned_end_time.slice(0, 5)}
                            </div>
                          ) : session.planned_time ? (
                            <div className="flex items-center justify-center gap-1 text-xs text-muted-foreground">
                              <Clock className="w-3 h-3" />
                              {session.planned_time.slice(0, 5)}
                            </div>
                          ) : null}

                          {session.notes && (
                            <p className="text-xs text-muted-foreground line-clamp-2 mt-1">{session.notes}</p>
                          )}
                        </div>

                        {/* Edit and Cancel buttons - only visible to session owner */}
                        {session.user_id === currentUserId && user && (
                          <div className="flex gap-2 w-full mt-2 pt-2 border-t border-border">
                            <Button
                              variant="outline"
                              size="sm"
                              className="flex-1 text-xs"
                              onClick={() => handleEditPlan(session)}
                            >
                              <Edit className="w-3 h-3 mr-1" />
                              Edit
                            </Button>
                            <Button
                              variant="destructive"
                              size="sm"
                              className="flex-1 text-xs"
                              onClick={() => handleRemovePlan(session.id)}
                            >
                              <Trash2 className="w-3 h-3 mr-1" />
                              Cancel
                            </Button>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {!isPlus && (
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <div className="pointer-events-auto">
            <Button 
              onClick={onJoinPlan}
              className="bg-blue-600 hover:bg-blue-500 text-white rounded-[2rem] h-14 px-8 font-black uppercase tracking-widest shadow-xl shadow-blue-600/20 active:scale-95 transition-all"
            >
              <Lock className="w-4 h-4 mr-2" />
              {user ? "Upgrade to Plus" : "Join Community"}
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}
